import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, DotsSixVertical } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = [
  { value: 'gravida', label: 'Saúde Materna' },
  { value: 'pre_concecao', label: 'Pré-conceção' },
  { value: 'pos_parto', label: 'Pós-parto' },
  { value: 'introducao_alimentar', label: 'Introdução alimentar' },
  { value: 'emagrecimento', label: 'Perda de peso' },
];
const Q_TYPES = [
  { value: 'short_text', label: 'Texto curto' },
  { value: 'long_text', label: 'Texto longo' },
  { value: 'single_choice', label: 'Escolha única' },
  { value: 'multi_choice', label: 'Escolha múltipla' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'scale', label: 'Escala 1-10' },
  { value: 'yes_no', label: 'Sim/Não' },
];

export default function PreConsultaPage() {
  useRequireAdmin();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conditions, setConditions] = useState('');
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [patientTags, setPatientTags] = useState([]);

  const fetchAll = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/pre-consultation/templates`); setTemplates(data); } catch {}
    try { const { data } = await axios.get(`${API}/api/patient-tags`); setPatientTags(data?.tags || []); } catch {}
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadTemplate = async (prog) => {
    setSelected(prog);
    try {
      const { data } = await axios.get(`${API}/api/admin/pre-consultation/templates/${prog}`);
      setConditions(data?.conditions_text || '');
      setQuestions(data?.questions || []);
    } catch { setConditions(''); setQuestions([]); }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/pre-consultation/templates/${selected}`, { conditions_text: conditions, questions });
      toast.success('Template guardado'); fetchAll();
    } catch { toast.error('Erro'); }
    finally { setSaving(false); }
  };

  const addQ = () => setQuestions([...questions, { type: 'short_text', label: '', required: false, options: [] }]);
  const updateQ = (i, field, val) => { const q = [...questions]; q[i] = { ...q[i], [field]: val }; setQuestions(q); };
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  // Drag-and-drop
  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = (e, targetIdx) => {
    e.preventDefault(); setOverIdx(null);
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...questions];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx > dragIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setQuestions(arr);
    setDragIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div className="fade-in" data-testid="pre-consulta-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Questionários Pré-Consulta</h2>
          <p className="text-xs text-brand-muted mt-0.5">Onboarding obrigatório antes da primeira consulta</p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 flex-wrap">
        {PROGRAMS.map(p => {
          const has = templates.some(t => t.program_type === p.value);
          return (
            <button key={p.value} onClick={() => loadTemplate(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${selected === p.value ? 'bg-brand-green text-white' : has ? 'bg-brand-green/10 text-brand-green border border-brand-green/20' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
              {p.label} {has && selected !== p.value && '*'}
            </button>
          );
        })}
      </div>

      {!selected ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <p className="text-brand-muted text-sm">Seleciona um programa para editar o questionário.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <Label className="text-xs text-brand-muted mb-2 block">Condições do programa (texto que a cliente aceita)</Label>
            <Textarea value={conditions} onChange={e => setConditions(e.target.value)} rows={6} className="rounded-xl border-brand-border text-sm" placeholder="Escreve aqui as condições do programa..." data-testid="conditions-text" />
          </div>

          <div className="bg-white border border-brand-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-brand-muted">Perguntas do questionário</Label>
              <button onClick={addQ} className="text-xs text-brand-green flex items-center gap-1" data-testid="add-question-btn"><Plus size={12} /> Adicionar pergunta</button>
            </div>
            {questions.length === 0 ? <p className="text-xs text-brand-placeholder py-4">Sem perguntas. Adiciona a primeira.</p> : (
              <div className="space-y-2">
                {questions.map((q, i) => (
                  <div key={i}
                    draggable onDragStart={e => handleDragStart(e, i)} onDragEnd={handleDragEnd}
                    onDragOver={e => handleDragOver(e, i)} onDrop={e => handleDrop(e, i)}
                    className={`bg-brand-bg/30 rounded-xl p-3 space-y-2 transition-all ${dragIdx === i ? 'opacity-40' : ''} ${overIdx === i && dragIdx !== i ? 'ring-2 ring-brand-green/40 bg-brand-green/5' : ''}`}
                    data-testid={`question-${i}`}>
                    <div className="flex gap-2 items-center">
                      <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green flex-shrink-0"><DotsSixVertical size={14} /></div>
                      <Input value={q.label} onChange={e => updateQ(i, 'label', e.target.value)} placeholder="Texto da pergunta" className="flex-1 rounded-lg border-brand-border text-sm h-8" data-testid={`q-label-${i}`} />
                      <select value={q.type} onChange={e => updateQ(i, 'type', e.target.value)} className="border border-brand-border rounded-lg px-2 text-xs bg-white h-8">
                        {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-3 pl-6">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={q.required} onChange={e => updateQ(i, 'required', e.target.checked)} className="rounded" />
                        <span className="text-[10px] text-brand-muted">Obrigatória</span>
                      </label>
                      {(q.type === 'single_choice' || q.type === 'multi_choice') && (
                        <Input value={(q.options || []).join(', ')} onChange={e => updateQ(i, 'options', e.target.value.split(',').map(o => o.trim()).filter(Boolean))} placeholder="Opções (separadas por vírgula)" className="flex-1 rounded-lg border-brand-border text-[10px] h-7" />
                      )}
                      {q.type === 'yes_no' && (
                        <select value={q.sync_tag || ''} onChange={e => updateQ(i, 'sync_tag', e.target.value)} className="border border-brand-border rounded-lg px-2 text-[10px] bg-white h-7" data-testid={`q-sync-tag-${i}`}>
                          <option value="">Sem tag associada</option>
                          {patientTags.map(t => <option key={t.value} value={t.value}>Sim → tag "{t.label}"</option>)}
                        </select>
                      )}
                      <button onClick={() => removeQ(i)} className="text-brand-placeholder hover:text-red-500 p-0.5 ml-auto"><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={save} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-pre-consult-btn">
            <FloppyDisk size={16} /> {saving ? 'A guardar...' : 'Guardar template'}
          </button>
        </div>
      )}
    </div>
  );
}
